import { QueryKeys } from "@/constants/query-keys";
import { useIsSubscriptionEnabled } from "@/hooks/use-is-subscription-enabled";
import { getOfferings } from "@/utils/revenue-cat";
import { PACKAGE_TYPE } from "@revenuecat/purchases-capacitor";
import { useQuery } from "@tanstack/react-query";

interface UseSubscriptionOfferingsOptions {
  useMockData?: boolean;
}

const createMockOfferings = () =>
  ({
    current: {
      availablePackages: [
        {
          identifier: "$rc_monthly",
          packageType: PACKAGE_TYPE.MONTHLY,
          product: {
            identifier: "nutricoach_monthly",
            title: "Monthly",
            description: "Monthly plan",
            price: 9.99,
            priceString: "£9.99",
            subscriptionPeriod: "P1M",
          },
        },
        {
          identifier: "$rc_annual",
          packageType: PACKAGE_TYPE.ANNUAL,
          product: {
            identifier: "nutricoach_annual",
            title: "Yearly",
            description: "Yearly plan",
            price: 59,
            priceString: "£59",
            subscriptionPeriod: "P1Y",
          },
        },
      ],
    },
  }) as Awaited<ReturnType<typeof getOfferings>>;

export const useSubscriptionOfferings = (options?: UseSubscriptionOfferingsOptions) => {
  const { useMockData = false } = options ?? {};
  const { isEnabled: isSubscriptionEnabled } = useIsSubscriptionEnabled();
  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: [
      QueryKeys.SUBSCRIPTION_OFFERINGS,
      useMockData ? "mock" : "live",
      isSubscriptionEnabled ? "enabled" : "disabled",
    ],
    queryFn: async () => {
      if (useMockData) {
        return createMockOfferings();
      }
      const offerings = await getOfferings(isSubscriptionEnabled);
      console.info("useSubscriptionProducts", offerings);
      if (!offerings) {
        throw new Error(
          "Subscription offerings are unavailable on this platform or not configured.",
        );
      }
      return offerings;
    },
    enabled: isSubscriptionEnabled,
  });

  return {
    offerings: data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};
